import { useSelect } from "@refinedev/antd";
import {
  DatePicker,
  Form,
  Input,
  Modal,
  Select,
  Typography,
  message,
} from "antd";
import dayjs, { Dayjs } from "dayjs";
import type { IShipco } from "interfaces";
import React, { useEffect, useState } from "react";
import { apiErrorMessage, useReactivateWithDate } from "./api";
import {
  formatDay,
  reactivateReason,
  reactivateRequest,
  UkhoDialogTarget,
} from "./lib";
import {
  DEACTIVATED_VESSELS_SHIPCO_NAME,
  useDeactivatedVesselsShipcoId,
} from "./useDeactivatedVesselsShipcoId";

export interface ReactivateWithDateModalProps {
  open: boolean;
  /** The vessel and the UKHO arriving notice (a work-list row or the vessel page's view). */
  target: UkhoDialogTarget;
  onClose: () => void;
  /** Called after the reactivation succeeded and its confirmation was shown. */
  onDone: () => void;
}

/**
 * Spec 11.2 reactivate dialog: the existing reactivation (POST
 * /vessel-transfer, from_old_vessels) to the shipco the vessel left at its
 * last park, with the date and the reason taken from the UKHO notice. The
 * Edit Vessel step of the full form is skipped.
 */
export const ReactivateWithDateModal: React.FC<ReactivateWithDateModalProps> = ({
  open,
  target,
  onClose,
  onDone,
}) => {
  const deactivatedShipcoId = useDeactivatedVesselsShipcoId();
  const { mutate: reactivate, mutation } = useReactivateWithDate();
  const [targetShipcoId, setTargetShipcoId] = useState<number | null>(
    target.previousShipcoId
  );
  const [effectiveDay, setEffectiveDay] = useState<Dayjs>(() =>
    dayjs(target.day)
  );
  const [reason, setReason] = useState(() =>
    reactivateReason(target.day, target.transferId)
  );

  // As the New transfer form does: a reactivation never goes back to Deactivated Vessels.
  const { selectProps: shipcoSelectProps } = useSelect<IShipco>({
    resource: "shipco",
    optionLabel: "registrationName",
    optionValue: "id",
    pagination: { mode: "server", pageSize: 100 },
    filters:
      deactivatedShipcoId !== null
        ? [{ field: "id", operator: "ne", value: deactivatedShipcoId }]
        : [],
    defaultValue: target.previousShipcoId ?? undefined,
  });

  // Each opening starts from the previous customer and the notice's day and reason.
  useEffect(() => {
    if (open) {
      setTargetShipcoId(target.previousShipcoId);
      setEffectiveDay(dayjs(target.day));
      setReason(reactivateReason(target.day, target.transferId));
    }
  }, [open, target.previousShipcoId, target.day, target.transferId]);

  const submitReactivation = () => {
    if (targetShipcoId === null) {
      return;
    }
    const effectiveDate = effectiveDay.format("YYYY-MM-DD");
    reactivate(
      reactivateRequest(target, targetShipcoId, effectiveDate, reason.trim()),
      {
        onSuccess: () => {
          message.success(
            `${target.vesselName} reactivated with effective date ${formatDay(effectiveDate)}`
          );
          onDone();
        },
        onError: (error) => {
          message.error(apiErrorMessage(error));
        },
      }
    );
  };

  return (
    <Modal
      open={open}
      title={`Reactivate ${target.vesselName}`}
      okText="Reactivate"
      cancelText="Cancel"
      onOk={submitReactivation}
      onCancel={onClose}
      okButtonProps={{ disabled: targetShipcoId === null }}
      confirmLoading={mutation.isPending}
      width={560}
    >
      <Typography.Paragraph type="secondary">
        The same reactivation as on the Vessel Transfers screen, with the date
        and reason filled in from the UKHO notice.
      </Typography.Paragraph>
      <Form layout="horizontal" labelCol={{ span: 6 }} wrapperCol={{ span: 18 }}>
        <Form.Item label="Ship" style={{ marginBottom: 8 }}>
          <Typography.Text strong>{target.vesselName}</Typography.Text>{" "}
          <Typography.Text type="secondary">{target.imoNumber}</Typography.Text>
        </Form.Item>
        <Form.Item label="From" style={{ marginBottom: 8 }}>
          {DEACTIVATED_VESSELS_SHIPCO_NAME}
        </Form.Item>
        <Form.Item label="To" extra="previous customer">
          <Select
            options={shipcoSelectProps.options}
            onSearch={shipcoSelectProps.onSearch}
            loading={shipcoSelectProps.loading}
            value={targetShipcoId ?? undefined}
            placeholder="choose the customer"
            onChange={(shipcoId: number) => setTargetShipcoId(Number(shipcoId))}
            showSearch
            filterOption={false}
            style={{ width: "100%" }}
          />
        </Form.Item>
        <Form.Item label="Effective date" extra="UKHO arriving day">
          <DatePicker
            value={effectiveDay}
            onChange={(day) => day && setEffectiveDay(day)}
            allowClear={false}
            format="D MMM YYYY"
          />
        </Form.Item>
        <Form.Item label="Reason">
          <Input.TextArea
            rows={2}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};
